import {
  keepPreviousData, useMutation, useQuery, useQueryClient,
} from '@tanstack/react-query';

import identity from 'utils/identity';

import {
  createVentureBuilder,
  createManagedStartup,
  updateManagedStartup,
  getManagedStartups,
  getVentureBuilderById,
  getVentureBuilders,
  updateVentureBuilder,
} from '../api';
import {
  CreateVentureBuilderPayload,
  ManagedStartupInput,
  EditVentureBuilderPayload,
  VentureBuildersQuery,
} from '../types';
import { VENTURE_BUILDER_QUERY_KEYS } from '../utils/constants';

export const useVentureBuilders = (query: VentureBuildersQuery) => useQuery({
  queryKey: VENTURE_BUILDER_QUERY_KEYS.list(query),
  queryFn: () => getVentureBuilders(query),
  placeholderData: keepPreviousData,
  refetchOnWindowFocus: false,
});

export const useVentureBuilder = (id: number) => useQuery({
  queryKey: VENTURE_BUILDER_QUERY_KEYS.details(id),
  queryFn: () => getVentureBuilderById(id),
  enabled: identity.isPositiveNumber(id),
  refetchOnWindowFocus: false,
  retry: false,
});

export const useManagedStartups = (ventureBuilderId: number) => useQuery({
  queryKey: VENTURE_BUILDER_QUERY_KEYS.startups(ventureBuilderId),
  queryFn: () => getManagedStartups(ventureBuilderId),
  enabled: identity.isPositiveNumber(ventureBuilderId),
  refetchOnWindowFocus: false,
});

const useInvalidateVentureBuilders = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: VENTURE_BUILDER_QUERY_KEYS.all });
};

export const useCreateVentureBuilder = () => {
  const invalidate = useInvalidateVentureBuilders();
  return useMutation({
    mutationFn: (payload: CreateVentureBuilderPayload) => createVentureBuilder(payload),
    onSuccess: invalidate,
  });
};

export const useUpdateVentureBuilder = (id: number) => {
  const invalidate = useInvalidateVentureBuilders();
  return useMutation({
    mutationFn: (payload: EditVentureBuilderPayload) => updateVentureBuilder(id, payload),
    onSuccess: invalidate,
  });
};

export const useCreateManagedStartup = (ventureBuilderId: number) => {
  const invalidate = useInvalidateVentureBuilders();
  return useMutation({
    mutationFn: (payload: ManagedStartupInput) => createManagedStartup(ventureBuilderId, payload),
    onSuccess: invalidate,
  });
};

export const useUpdateManagedStartup = () => {
  const invalidate = useInvalidateVentureBuilders();
  return useMutation({
    mutationFn: ({ id, name }: { id: number, name: string }) => updateManagedStartup(id, name),
    onSuccess: invalidate,
  });
};
